import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { verifyEmail } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleXmark,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';

const PRIMARY = 'rgb(37, 69, 252)';

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');
  const hasVerifiedRef = useRef(false);

  useEffect(() => {
    if (hasVerifiedRef.current) return;
    hasVerifiedRef.current = true;

    const token = params.get('token')?.trim();
    if (!token) {
      setStatus('error');
      setMessage('No verification token found in URL.');
      return;
    }

    verifyEmail(encodeURIComponent(token))
      .then(data => {
        if (data.access_token) {
          login(data.access_token, data.user);
          setStatus('success');
          setMessage(data.message || 'Email verified successfully.');
          toast.success('Email verified. Redirecting to dashboard.');
          setTimeout(() => navigate('/dashboard', { replace: true }), 1200);
          return;
        }

        setStatus('success');
        setMessage(data.message || 'Email verified successfully. Please sign in.');
        toast.success('Email verified! Please sign in.');
        setTimeout(() => navigate('/login', { replace: true }), 2000);
      })
      .catch(err => {
        const msg = err.message || 'Verification failed. The link may have expired.';
        setStatus('error');
        setMessage(msg);
        toast.error(msg);
      });
  }, [params, login, navigate]);

  return (
    <div style={s.page}>
      <div style={s.card}>

        {/* LOGO */}
        <div style={s.logoWrap}>
          <img src="/logo-blue.png" alt="logo" style={s.logo} />
          <span style={s.brand}>DataSphere</span>
        </div>

        {/* VERIFYING */}
        {status === 'verifying' && (
          <>
            <div style={s.iconWrap}>
              <FontAwesomeIcon icon={faSpinner} spin />
            </div>
            <h2 style={s.title}>Verifying your email</h2>
            <p style={s.sub}>Please wait while we confirm your account.</p>
          </>
        )}

        {/* SUCCESS */}
        {status === 'success' && (
          <>
            <div style={{ ...s.iconWrap, color: PRIMARY }}>
              <FontAwesomeIcon icon={faCircleCheck} />
            </div>

            <h2 style={s.title}>Email Verified Successfully</h2>
            <p style={s.sub}>{message}</p>

            <p style={s.note}>Redirecting you to login...</p>

            <Link to="/login" style={s.primaryBtn}>
              Continue to Sign In →
            </Link>
          </>
        )}

        {/* ERROR */}
        {status === 'error' && (
          <>
            <div style={{ ...s.iconWrap, color: '#ef4444' }}>
              <FontAwesomeIcon icon={faCircleXmark} />
            </div>

            <h2 style={s.title}>Verification Failed</h2>
            <p style={s.sub}>{message}</p>

            <div style={s.actions}>
              <Link to="/register" style={s.primaryBtn}>
                Create Account
              </Link>

              <Link to="/login" style={s.secondaryBtn}>
                Go to Login
              </Link>
            </div>
          </>
        )}

      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg,#eef2ff,#f8fafc)',
    padding: 24,
  },

  card: {
    width: '100%',
    maxWidth: 440,
    background: '#fff',
    borderRadius: 20,
    padding: '42px 32px',
    textAlign: 'center',
    boxShadow: '0 25px 70px rgba(0,0,0,0.08)',
    border: '1px solid rgba(0,0,0,0.04)',
  },

  /* LOGO */
  logoWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 26,
  },

  logo: {
    width: 30,
    height: 30,
    objectFit: 'contain',
  },

  brand: {
    fontWeight: 700,
    fontSize: 17,
  },

  iconWrap: {
    fontSize: 44,
    marginBottom: 18,
    color: PRIMARY,
  },

  title: {
    fontSize: 22,
    fontWeight: 800,
    marginBottom: 10,
    color: '#111827',
  },

  sub: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 1.6,
  },

  note: {
    fontSize: 12,
    color: '#9ca3af',
    marginTop: 6,
    marginBottom: 14,
  },

  primaryBtn: {
    display: 'block',
    marginTop: 18,
    padding: '14px',
    borderRadius: 12,
    background: `linear-gradient(135deg, ${PRIMARY}, #6366f1)`,
    color: '#fff',
    fontWeight: 600,
    textDecoration: 'none',
    boxShadow: '0 10px 30px rgba(37,69,252,0.25)',
  },

  secondaryBtn: {
    display: 'block',
    marginTop: 10,
    padding: '12px',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    color: '#374151',
    textDecoration: 'none',
    fontWeight: 500,
  },

  actions: {
    marginTop: 16,
  },
};
